import type { ComponentType, SVGProps } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import BookOpen from "lucide-react/dist/esm/icons/book-open.js";
import ChefHat from "lucide-react/dist/esm/icons/chef-hat.js";
import Heart from "lucide-react/dist/esm/icons/heart.js";
import MessageCircle from "lucide-react/dist/esm/icons/message-circle.js";
import Sparkles from "lucide-react/dist/esm/icons/sparkles.js";
import UserCheck from "lucide-react/dist/esm/icons/user-check.js";
import Users from "lucide-react/dist/esm/icons/users.js";
import { useSiteFeatureFlags } from "@shared/hooks/use-site-feature-flags";
import { isFeatureOn } from "@shared/lib/site-features";
import { cn } from "@shared/lib/utils";

const SUPPORTED_LANGS = ["en", "ar", "de", "es", "fr"];

function stripLang(pathname: string): string {
  for (const lang of SUPPORTED_LANGS) {
    if (pathname.startsWith(`/${lang}/`)) return pathname.slice(lang.length + 1);
    if (pathname === `/${lang}`) return "/";
  }
  return pathname;
}

function getPathLanguage(pathname: string): string | null {
  const segment = pathname.split("/")[1];
  return segment && SUPPORTED_LANGS.includes(segment) ? segment : null;
}

function localizeHref(href: string, pathname: string): string {
  const lang = getPathLanguage(pathname);
  if (!lang || href === "/") return href;
  return `/${lang}${href}`;
}

interface TabItem {
  id: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  labelKey: string;
  /** Site feature registry key (`apps/core/features/registry.py`). */
  featureFlag: string;
  isActive: (path: string) => boolean;
}

const TAB_ITEMS: TabItem[] = [
  {
    id: "hope",
    featureFlag: "community.hope",
    href: "/hope",
    icon: Heart,
    labelKey: "community.shell.tabs.hope",
    isActive: (p) => p === "/hope",
  },
  {
    id: "journal",
    featureFlag: "community.gratitude",
    href: "/gratitude",
    icon: Sparkles,
    labelKey: "community.shell.tabs.journal",
    isActive: (p) => p === "/gratitude",
  },
  {
    id: "similar",
    featureFlag: "community.similar_users",
    href: "/similar-users",
    icon: Users,
    labelKey: "community.shell.tabs.similar",
    isActive: (p) => p === "/similar-users",
  },
  {
    id: "forums",
    featureFlag: "community",
    href: "/community",
    icon: MessageCircle,
    labelKey: "community.shell.tabs.forums",
    isActive: (p) =>
      p === "/community" ||
      p.startsWith("/community/forums") ||
      p.startsWith("/community/threads"),
  },
  {
    id: "stories",
    featureFlag: "community.stories",
    href: "/community/stories",
    icon: BookOpen,
    labelKey: "community.shell.tabs.stories",
    isActive: (p) => p === "/community/stories" || p.startsWith("/community/stories/"),
  },
  {
    id: "mentors",
    href: "/community/mentors",
    icon: UserCheck,
    labelKey: "community.shell.tabs.mentors",
    featureFlag: "community.mentors",
    isActive: (p) => p === "/community/mentors" || p.startsWith("/community/mentors/"),
  },
  {
    id: "recipes",
    featureFlag: "community.recipes",
    href: "/community/recipes",
    icon: ChefHat,
    labelKey: "community.shell.tabs.recipes",
    isActive: (p) => p === "/community/recipes" || p.startsWith("/community/recipes/"),
  },
];

// Static class names so Tailwind keeps them; hidden tabs leave no gaps.
const GRID_COLS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
  7: "grid-cols-7",
};

export function CommunitySubNav() {
  const { t } = useTranslation("community");
  const location = useLocation();
  const { data: featureFlags } = useSiteFeatureFlags();
  const pathname = stripLang(location.pathname);
  // Fail closed: a tab shows only when its feature is on.
  const visibleTabItems = TAB_ITEMS.filter((item) =>
    isFeatureOn(featureFlags, item.featureFlag),
  );

  return (
    <nav
      aria-label={t("community.shell.tabsAriaLabel", "Community sections")}
      data-testid="community-sub-nav"
      className={cn(
        // Mobile: fixed bottom tab bar
        "fixed bottom-0 start-0 end-0 z-40 border-t bg-background/95 backdrop-blur",
        // Tablet: sticky sub-bar below global header (header is h-16 = 64px)
        "sm:sticky sm:top-16 sm:bottom-auto sm:start-auto sm:end-auto sm:z-30 sm:border-b sm:border-t-0",
        // Desktop: hidden (global side nav is sufficient)
        "lg:hidden",
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className={cn(
          "grid sm:flex sm:overflow-x-auto sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden",
          GRID_COLS[visibleTabItems.length] ?? "grid-cols-7",
        )}
        role="list"
      >
        {visibleTabItems.map((item) => {
          const active = item.isActive(pathname);
          const Icon = item.icon;
          const href = localizeHref(item.href, location.pathname);
          return (
            <a
              key={item.id}
              href={href}
              role="listitem"
              aria-current={active ? "page" : undefined}
              className={cn(
                // Base layout
                "relative flex min-h-[44px] min-w-0 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs transition-colors",
                // Tablet: row layout, not flex-1
                "sm:min-h-[44px] sm:flex-none sm:flex-row sm:gap-1.5 sm:px-4",
                // Active indicator: top border on mobile (tab is at bottom), bottom border on tablet
                "border-t-2 sm:border-t-0 sm:border-b-2",
                active
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="line-clamp-1 max-w-full text-center leading-tight sm:text-start">
                {t(item.labelKey, item.id)}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
